import { observable, action } from 'mobx';
import * as eftPrjAdminApi from 'cd-eft/services/eftPrjAdminApi';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { getErrorFromAPIResponse } from 'cd-eft/utils/getError';

export class AdminProjectOwnerVM {
  history: any;

  constructor(history) {
    this.history = history;
  }

  @observable isLoading: boolean = false;
  @observable userData: any[] = [];
  @observable filteredUserData: any[] = [];
  @observable userSuggestions: any[] = [];

  @observable selectedUser: any = {};
  @observable selectedUserProjects: any[] = [];
  @observable selectedUserFilteredProjects: any[] = [];
  @observable projectsToChange: Set<string> = new Set();

  @observable displayModal: boolean = false;
  @observable displayConfirmation: boolean = false;

  @observable newOwnerName: string = '';
  @observable newOwner: any = {};

  @observable userNameSearchString: string = '';
  @observable projectNameSearchString: string = '';
  @observable actionOption: string = '';

  @observable allProjectsSelected: boolean = false;

  @action.bound setAllProjectsSelected = (e) => {
    this.allProjectsSelected = e.target.checked;
    if (e.target.checked) {
      this.projectsToChange = new Set();
      this.selectedUserFilteredProjects.forEach((el) => this.projectsToChange.add(el.projectUuid));
    } else {
      this.projectsToChange = new Set();
    }
  };

  @action.bound setActionOption = (e) => (this.actionOption = e.target.value);

  @action.bound init = async () => {
    this.isLoading = true;
    try {
      const response = await eftPrjAdminApi.EftProjectController.getProjectsWithCountByOwnerUsingGET();
      if (response && response.data) {
        this.userData = response.data;
        this.filteredUserData = response.data;
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.isLoading = false;
    }
  };

  @action.bound getUserProjects = (userId) => {
    if (userId && this.userData.length > 0) {
      this.onClickClearAll();
      const user = this.userData.find((el) => el.coreUserId == userId);
      if (user) {
        this.selectedUser = user;
        this.viewProjects(user);
      }
    }
  };

  @action.bound searchByUserName = (e) => {
    this.userNameSearchString = e.target.value;
    this.filteredUserData = this.userData.filter((el) =>
      el.userName.toLowerCase().includes(e.target.value.toLowerCase())
    );
  };

  @action.bound searchByProjectName = (e) => {
    this.projectNameSearchString = e.target.value;
    this.projectsToChange = new Set();
    this.allProjectsSelected = false;
    this.selectedUserFilteredProjects = this.selectedUserProjects.filter((el) =>
      el.projectTitle.toLowerCase().includes(e.target.value.toLowerCase())
    );
  };

  @action openModalChangeOwner = (user) => {
    if (this.selectedUser.coreUserId !== user.coreUserId) this.selectedUser = user;
    this.newOwnerName = '';
    this.displayModal = !this.displayModal;
  };

  @action.bound toggleModal = () => (this.displayModal = !this.displayModal);
  @action.bound saveSelection = () => (this.displayConfirmation = true);

  @action.bound cancelSelection = () => {
    this.userSuggestions = [];
    this.displayModal = false;
    this.displayConfirmation = false;
    this.newOwner = {};
    this.newOwnerName = '';
    this.actionOption = '';
  };

  // button clears project selection and search by projectName
  @action.bound onClickClearAll = () => {
    this.projectsToChange = new Set();
    this.allProjectsSelected = false;
    this.actionOption = '';
    this.projectNameSearchString = '';
    this.selectedUserFilteredProjects = this.selectedUserProjects;
  };

  // clears all except userData when exiting View Projects page, and after update Project API call
  @action.bound clearAll = () => {
    this.userSuggestions = [];
    this.displayModal = false;
    this.displayConfirmation = false;
    this.newOwner = {};
    this.newOwnerName = '';
    this.selectedUser = {};
    this.selectedUserProjects = [];
    this.selectedUserFilteredProjects = [];
    this.projectsToChange = new Set();
    this.allProjectsSelected = false;
    this.actionOption = '';
    this.userNameSearchString = '';
  };

  @action.bound selectNewOwner = (user) => {
    this.newOwner = user;
    this.newOwnerName = user.userName;
    this.userSuggestions.length = 0;
  };

  @action.bound onChangeNewOwnerName = (e) => {
    const value = e.target.value;
    this.newOwnerName = value;
    if (value) {
      this.userSuggestions = this.userData.filter((user) => user.userName.toLowerCase().includes(value.toLowerCase()));
    } else {
      this.userSuggestions.length = 0;
    }
  };

  @action.bound viewProjects = async (user) => {
    this.isLoading = true;
    this.selectedUserProjects.length = 0;
    this.selectedUserFilteredProjects.length = 0;
    this.selectedUser = user;
    try {
      const response: any = await eftPrjAdminApi.EftProjectController.getAllProjectsByOwnerNameUsingGET(user.userEmail);

      if (response && response.data) {
        this.selectedUserProjects = response.data;
        this.selectedUserFilteredProjects = response.data;
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      this.isLoading = false;
    }
  };

  @action saveChangingOwner = async () => {
    this.isLoading = true;

    let listOfProjects: string[] = [];
    if (this.projectsToChange.size === 0) {
      await this.viewProjects(this.selectedUser);
      this.selectedUserProjects.forEach((el) => listOfProjects.push(el.projectUuid));
    } else {
      listOfProjects = Array.from(this.projectsToChange);
    }

    const payload = {
      coreUserId: this.newOwner.coreUserId,
      projectUuidList: listOfProjects,
    };

    try {
      const response = await eftPrjAdminApi.EftProjectController.updateProjectOwnerUsingPOST(payload);
      console.log('response:', response);

      if (response) {
        const newUserId = this.newOwner.coreUserId;
        this.clearAll();
        await this.init();
        this.history.push(`/admin/project-owner/${newUserId}`);
      }
    } catch (error) {
      getErrorFromAPIResponse(error);
    } finally {
      this.isLoading = false;
    }
  };

  @action.bound setProjectToChange = (e) => {
    const { checked, value } = e.target;
    if (checked) {
      this.projectsToChange.add(value);
    } else {
      if (this.projectsToChange.has(value)) this.projectsToChange.delete(value);
      this.allProjectsSelected = false;
    }
  };
}
